import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from '../../../components/modals';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/types';
import { toggleDataBrowserModal } from '../../../store/datasources';
import { DataSourceHandler, ConnectionAccessDto } from '../../../api/data-source-handler';
import { TagFieldOption, TagFieldWithSuggestions } from '../../../components/form/tag-field-suggestions';
import { successAlert, errorAlert } from '../../../components/toastify/notify-toast';
import { InPageSpinner } from '../../../components/spinners/in-page-spinner';
import { isEmpty } from 'lodash';

interface ShareConnectionAccessModalProps {
    connectionInfo: { id: number; name: string };
}

export const ShareConnectionAccessModal: React.FC<ShareConnectionAccessModalProps> = ({ connectionInfo }) => {
    const dispatch = useDispatch();
    const { shareConnectionAccess: showModal } = useSelector((store: RootState) => store.NewDataSourceReducer.showModal);
    const { envVariables: Env, allUsersList } = useSelector((store: RootState) => store.AccountReducer);
    const [accessList, setAccessList] = useState<ConnectionAccessDto[]>([]);
    const [isLoadingList, setIsLoadingList] = useState(false);
    const [isGranting, setIsGranting] = useState(false);
    const [removingUserId, setRemovingUserId] = useState<string | null>(null);
    const [selectedUsers, setSelectedUsers] = useState<TagFieldOption[]>([]);
    const [pendingUsers, setPendingUsers] = useState<TagFieldOption[]>([]);
    const [tagFieldResetKey, setTagFieldResetKey] = useState(0);

    const usersListForTagField: TagFieldOption[] = useMemo(
        () => allUsersList.map((user) => ({ label: `${user.firstName} ${user.lastName ? user.lastName.charAt(0) : ''} (${user.email})`, value: user.email, textForTag: user.email })),
        [allUsersList]
    );

    const toggleClose = useCallback(() => {
        dispatch(toggleDataBrowserModal('shareConnectionAccess', false));
        setSelectedUsers([]);
        setPendingUsers([]);
        setAccessList([]);
    }, [dispatch]);

    const fetchAccessList = useCallback(() => {
        if (!connectionInfo?.id || !Env?.REACT_APP_PLATFORM_URL) return;
        setIsLoadingList(true);
        DataSourceHandler.GetConnectionAccessList(
            `${Env.REACT_APP_PLATFORM_URL}/databrowser/api`,
            connectionInfo.id,
            (res: ConnectionAccessDto[]) => {
                setAccessList(res || []);
                setIsLoadingList(false);
            },
            () => setIsLoadingList(false)
        );
    }, [connectionInfo?.id, Env?.REACT_APP_PLATFORM_URL]);

    useEffect(() => {
        if (showModal) fetchAccessList();
    }, [showModal, fetchAccessList]);

    /*
     * Moves the selected users into the pending list — no API call yet, access is only
     * granted once the user confirms via Done
     */
    const handleAddPendingUsers = useCallback(() => {
        if (isEmpty(selectedUsers)) return;
        setPendingUsers((prev) => {
            const existingValues = new Set([...prev.map((user) => user.value), ...accessList.map((access) => access.userId)]);
            const newUsers = selectedUsers.filter((user) => !existingValues.has(user.value));
            return [...prev, ...newUsers];
        });
        setSelectedUsers([]);
        setTagFieldResetKey((key) => key + 1);
    }, [selectedUsers, accessList]);

    /*
     * Removes a user from the pending list before it's ever granted — a local-only undo
     */
    const handleRemovePendingUser = useCallback((value: string) => {
        setPendingUsers((prev) => prev.filter((user) => user.value !== value));
    }, []);

    /*
     * Grants access to every pending user (if any) and closes the modal — this is the only
     * point at which access is actually granted
     */
    const handleConfirmSharing = useCallback(() => {
        if (isEmpty(pendingUsers)) {
            toggleClose();
            return;
        }
        if (!Env?.REACT_APP_PLATFORM_URL) return;
        setIsGranting(true);
        const userIds = pendingUsers.map((user) => user.value);
        DataSourceHandler.GrantConnectionAccess(
            `${Env.REACT_APP_PLATFORM_URL}/databrowser/api`,
            { connectionId: connectionInfo.id, userIds, accessLevel: 'READ' },
            () => {
                userIds.forEach((userId) => successAlert(`Access granted to ${userId}.`));
                setIsGranting(false);
                toggleClose();
            },
            (error: any) => {
                errorAlert(error?.data || 'Failed to grant access. Please try again.');
                setIsGranting(false);
            }
        );
    }, [pendingUsers, Env?.REACT_APP_PLATFORM_URL, connectionInfo?.id, toggleClose]);

    const handleRevokeAccess = useCallback((userId: string) => {
        if (!Env?.REACT_APP_PLATFORM_URL) return;
        setRemovingUserId(userId);
        DataSourceHandler.RevokeConnectionAccess(
            `${Env.REACT_APP_PLATFORM_URL}/databrowser/api`,
            { connectionId: connectionInfo.id, userIds: [userId] },
            () => {
                successAlert(`Access revoked from ${userId}.`);
                fetchAccessList();
                setRemovingUserId(null);
            },
            (error: any) => {
                errorAlert(error?.data || 'Failed to revoke access. Please try again.');
                setRemovingUserId(null);
            }
        );
    }, [Env?.REACT_APP_PLATFORM_URL, connectionInfo?.id, fetchAccessList]);

    return (
        <Modal
            isOpen={showModal}
            toggleClose={toggleClose}
            title={`Share "${connectionInfo?.name}"`}
            subtitle="Add users for read-only access."
            className="profileModal__container shareConnectionAccessModal__container"
            showCloseMark
        >
            <div className="shareConnectionAccessModal__addUser">
                <TagFieldWithSuggestions
                    key={tagFieldResetKey}
                    options={usersListForTagField}
                    label="Add Users (Read-Only Access)"
                    handleSelectOptions={setSelectedUsers}
                    autoFocus
                />
                <button
                    className="btn-md btn-yellow"
                    type="button"
                    disabled={isEmpty(selectedUsers)}
                    onClick={handleAddPendingUsers}
                >
                    Add User
                </button>
            </div>

            {!isEmpty(pendingUsers) && (
                <div className="shareConnectionAccessModal__list">
                    <p className="shareConnectionAccessModal__listTitle">Pending — Not Yet Shared</p>
                    {pendingUsers.map((user) => (
                        <div key={user.value} className="shareConnectionAccessModal__listItem">
                            <span className="shareConnectionAccessModal__listItemEmail">{user.value}</span>
                            <span className="shareConnectionAccessModal__accessLevel shareConnectionAccessModal__accessLevel--pending">Pending</span>
                            <button
                                type="button"
                                className="btn-sm btn-cancel"
                                disabled={isGranting}
                                onClick={() => handleRemovePendingUser(user.value)}
                            >
                                Remove
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <div className="shareConnectionAccessModal__list">
                <p className="shareConnectionAccessModal__listTitle">Currently Shared With</p>
                {isLoadingList ? (
                    <InPageSpinner size="XSmall" />
                ) : isEmpty(accessList) ? (
                    <p className="shareConnectionAccessModal__emptyMsg">This connection isn&apos;t shared with anyone yet.</p>
                ) : (
                    accessList.map((access) => (
                        <div key={access.id} className="shareConnectionAccessModal__listItem">
                            <span className="shareConnectionAccessModal__listItemEmail">{access.userId}</span>
                            <span className="shareConnectionAccessModal__accessLevel">Read Only</span>
                            <button
                                type="button"
                                className="btn-sm btn-cancel"
                                disabled={removingUserId === access.userId}
                                onClick={() => handleRevokeAccess(access.userId)}
                            >
                                Remove
                            </button>
                        </div>
                    ))
                )}
            </div>

            <div className="modalBtns__box">
                <button className="btn-md btn-yellow" type="button" disabled={isGranting} onClick={handleConfirmSharing}>
                    {isGranting ? <InPageSpinner size="XSmall" color="black" /> : 'Done'}
                </button>
            </div>
        </Modal>
    );
};
